import { nanoid } from 'nanoid'
import { Auth } from 'aws-amplify'
import { table, restApiKey } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { getDynamoDB, getAppMaster } from '../../../../common/utils/DynamoDbUtils'
import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'

/**
 * 変換表一覧を取得する（IF02102）
 *
 * @param {string} corporationNumber
 * @returns {Promise<import('./types').ConversionTableRow[]>} Conversion_table_list（表示順ソート済み）
 */
export async function fetchConversionTableList(corporationNumber) {
  try {
    const res = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.listConversionTables(corporationNumber),
      { headers: {} }
    )
    return res.conversion_table_list
  } catch (err) {
    console.error('変換表一覧の取得に失敗しました:', err)
    throw err
  }
}

/**
 * 区分一覧を取得する（変換先区分ドロップダウン用）（IF02078: 区分一覧取得API） data_classification_display_order 昇順でソートして返す。
 *
 * @param {string} corporationNumber
 * @returns {Promise<import('./types').Classification[]>} 区分一覧（data_classification_display_order 昇順）
 */
export async function fetchClassificationList(corporationNumber) {
  if (!corporationNumber) return []

  try {
    // IF02078（区分一覧取得）を呼び出し
    const response = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getDataClassifications(corporationNumber),
      {
        headers: {},
        body: {}
      }
    )

    // レスポンスから区分リストを取得（bodyがある場合とない場合の両方に対応）
    const classifications = response?.body?.classifications || response?.classifications || []

    if (classifications.length === 0) {
      return []
    }

    // APIレスポンスは既にソート済みのため、そのまま返す
    return classifications
  } catch (err) {
    console.error('区分一覧の取得に失敗しました:', err)
    throw err
  }
}

/**
 * AppMaster テーブルから ChrofyClassification（区分マスター(CHROFY規定値)）のレコードを取得する（getAppMaster）
 *
 * AppMaster は CorporationNumber を持たないため汎用 getDynamoDB ではなく get_app_master 専用 API を使用する。
 * PK=ConfigName、SK=SearchKey。ConfigName = ChrofyClassification の行を取得し、ChrofyClassificationDisplayOrder 昇順で返す。
 *
 * @param {string} corporationNumber
 * @returns {Promise<Record<string, unknown>[]>} AppMaster の ChrofyClassification 行
 */
export async function fetchChrofyClassificationListFromAppMaster(corporationNumber) {
  if (!corporationNumber) return []

  try {
    const rows = await getAppMaster(corporationNumber, { ConfigName: 'ChrofyClassification' }, null)
    if (!Array.isArray(rows)) return []
    return rows.sort(
      (a, b) =>
        (a.ChrofyClassificationDisplayOrder ?? a.chrofyClassificationDisplayOrder ?? 0) -
        (b.ChrofyClassificationDisplayOrder ?? b.chrofyClassificationDisplayOrder ?? 0)
    )
  } catch (err) {
    console.error('ChrofyClassification（AppMaster）の取得に失敗しました:', err)
    throw err
  }
}

/**
 * AppMaster の ChrofyClassification 行を、変換先区分プルダウン用に IF02078 区分行と同形へ変換する（search_key / data_classification_name）
 *
 * @param {Record<string, unknown>[] | null | undefined} rows - FetchChrofyClassificationListFromAppMaster の戻り値
 * @returns {{ search_key: string; data_classification_name: string }[]}
 */
export function mapChrofyClassificationAppMasterRowsToDropdownItems(rows) {
  if (!Array.isArray(rows)) return []
  return rows
    .map((row) => {
      const key = row.ChrofyClassificationKey ?? row.chrofyClassificationKey ?? ''
      const searchKey = row.SearchKey ?? row.search_key ?? (key ? `${key}#ChrofyClassification` : '')
      const name = row.ChrofyClassificationName ?? row.chrofyClassificationName ?? ''
      if (!searchKey || !name) return null
      return { search_key: searchKey, data_classification_name: name }
    })
    .filter(Boolean)
}

/** 変換先区分の search_key が ChrofyClassification（AppMaster）由来か（例: `{uuid}#ChrofyClassification`） */
export function isChrofyClassificationConversionTarget(conversionTarget) {
  return typeof conversionTarget === 'string' && conversionTarget.endsWith('#ChrofyClassification')
}

/** 変換先区分の search_key が DataClassification（区分マスター / IF02078）由来か（例: `{code}#DataClassification`） */
export function isDataClassificationConversionTarget(conversionTarget) {
  return typeof conversionTarget === 'string' && conversionTarget.endsWith('#DataClassification')
}

/**
 * Chrofy の search_key から ChrofyClassificationKey（# より前）を取得する
 *
 * @param {string} conversionTarget
 * @returns {string} 空文字の場合は無効
 */
export function parseChrofyClassificationKeyFromSearchKey(conversionTarget) {
  if (!isChrofyClassificationConversionTarget(conversionTarget)) return ''
  const head = conversionTarget.split('#')[0] ?? ''
  return head.trim()
}

/**
 * AppMaster ChrofyClassificationItem から、指定 Chrofy 区分に属する項目値（ChrofyClassificationItemValue）一覧を取得する（変換後バリデーション用）
 *
 * @param {string} corporationNumber
 * @param {string} conversionTarget - Search_key（`{ChrofyClassificationKey}#ChrofyClassification`）
 * @returns {Promise<string[]>}
 */
export async function fetchChrofyClassificationItemValuesForValidation(corporationNumber, conversionTarget) {
  const chrofyKey = parseChrofyClassificationKeyFromSearchKey(conversionTarget)
  if (!corporationNumber || !chrofyKey) return []

  try {
    const data = await getAppMaster(corporationNumber, { ConfigName: 'ChrofyClassificationItem' }, null)
    if (!data || !Array.isArray(data)) return []

    return data
      .map((item) => {
        const classificationKey = item.ChrofyClassificationKey ?? item.chrofyClassificationKey ?? ''
        if (classificationKey !== chrofyKey) return null
        const value = item.ChrofyClassificationItemValue ?? item.chrofyClassificationItemValue ?? ''
        return value ? String(value) : null
      })
      .filter(Boolean)
  } catch (err) {
    console.error('ChrofyClassificationItem（AppMaster）の取得に失敗しました:', err)
    throw err
  }
}

/**
 * 変更履歴を取得する（getDynamoDB）
 *
 * @param {string} corporationNumber
 * @returns {Promise<import('./types').HistoryRow[]>} EntityType=history のレコード。HistoryUpdateDateTime 降順でソート済み
 */
export async function fetchConversionHistory(corporationNumber) {
  try {
    const rows = await getDynamoDB(
      table.ValueConversion,
      null,
      corporationNumber,
      { CorporationNumber: corporationNumber },
      { EntityType: 'history' }
    )
    if (!Array.isArray(rows)) return []
    return rows.sort((a, b) => new Date(b.HistoryUpdateDateTime || 0) - new Date(a.HistoryUpdateDateTime || 0))
  } catch (err) {
    console.error('変更履歴の取得に失敗しました:', err)
    throw err
  }
}

/**
 * 指定区分の区分項目値リストを取得する（バリデーション用）（IF02070）
 *
 * @param {string} corporationNumber
 * @param {string} conversionTarget 区分のsearch_key（変換先区分）
 * @param {string} standardFormatColumnName 標準フォーマット項目名（区分名を使用）
 * @returns {Promise<string[]>} Item_value の配列
 */
export async function fetchClassificationItemValues(corporationNumber, conversionTarget, standardFormatColumnName) {
  if (!conversionTarget || !standardFormatColumnName) return []

  try {
    // conversionTargetからclassification_codeを抽出（search_keyの形式: "C0001#..." → "C0001"）
    const classificationCode = conversionTarget.includes('#') ? conversionTarget.split('#')[0] : conversionTarget

    // IF02070: 区分項目マスター設定取得
    const response = await httpPost(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getDataClassificationItems(corporationNumber),
      {
        headers: { 'Content-Type': 'application/json' },
        body: {
          classification_code: classificationCode,
          standard_format_column_name: standardFormatColumnName
        }
      }
    )

    // レスポンスから区分項目リストを取得
    const items = response?.body?.items || response?.items || []

    // item_valueのみを抽出
    return items.map((item) => item.item_value).filter(Boolean)
  } catch (err) {
    console.error('区分項目値の取得に失敗しました:', err)
    throw err
  }
}

/**
 * IF02077: 変換項目取得（GET + クエリパラメータ）
 *
 * 変換項目本体（最大10万件）は Lambda 同期レスポンスの 6MB 上限・API Gateway の 29秒タイムアウトを超えるため、取得APIは本体を返さず S3 キャッシュファイルのキー
 * （s3_key）のみを返す。ここでは署名付きURL（getS3PresignedUrl, FileManagement / DataMapper と同方式）を取得して S3 から直接ダウンロードし、本体 JSON を返す。
 *
 * @param {string} corporationNumber
 * @param {string} conversionTableKey
 * @returns {Promise<import('./types').GetConversionValuesResponse>}
 */
export async function getConversionValues(corporationNumber, conversionTableKey) {
  try {
    const path = PathInfoList.Resource.getConversionValues(corporationNumber)
    const init = {
      headers: {},
      queryStringParameters: {
        conversion_table_key: conversionTableKey
      }
    }
    const res = await httpGet(restApiKey.CHROFY_REST_API, path, init)
    const s3Key = res?.s3_key ?? res?.body?.s3_key
    if (!s3Key) {
      throw new Error('変換項目のS3キーの取得に失敗しました')
    }

    // 署名付きURLを取得し、S3から直接ダウンロードする
    const presignedRes = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getS3PresignedUrl(corporationNumber),
      { headers: {}, queryStringParameters: { object_key_name: s3Key } }
    )
    const presignedUrl = presignedRes?.presigned_url ?? presignedRes?.body?.presigned_url
    if (!presignedUrl) {
      throw new Error('変換項目ファイルの署名付きURLの取得に失敗しました')
    }

    const fetchRes = await fetch(presignedUrl)
    if (!fetchRes.ok) {
      throw new Error(`変換項目ファイルの取得に失敗しました: ${fetchRes.status}`)
    }
    return await fetchRes.json()
  } catch (err) {
    console.error('変換項目の取得に失敗しました:', err)
    throw err
  }
}

/**
 * IF02076: 変換項目更新判定（POST） セグメント登録画面と同様、リクエストbodyはラップせずそのまま送信する。
 *
 * @param {string} corporationNumber
 * @param {import('./types').ConversionTableRequest} body
 * @returns {Promise<Object>} レスポンス（IF02071 に渡す場合はレスポンスの body 内を渡す）
 */
export async function checkConversionTable(corporationNumber, body) {
  const path = PathInfoList.Resource.checkConversionTableItems(corporationNumber)
  const init = {
    headers: { 'Content-Type': 'application/json' },
    body
  }
  return httpPost(restApiKey.CHROFY_REST_API, path, init)
}

/**
 * 変換項目ペイロード（JSON）を Amplify 共通バケットへアップロードする。
 *
 * 変換項目（最大10万件規模）は API Gateway のボディ上限（10MB）を超えるため、 FileManagement と同じ Amplify `Storage.put`（level: private）で S3
 * へ直接アップロードし、 登録IF（update_conversion_values）には S3 キーのみを送信する。
 *
 * `s3Path` は private プレフィックス抜きの相対キー（`master/value-conversion/pending/{uuid}.json`）で、 Amplify が実際には
 * `private/{identityId}/{s3Path}` に配置する。 `user_id_sub` は FileManagement registerFiles と同じく
 * `Auth.currentUserCredentials()` の identityId。
 *
 * @param {Object} payload - { conversion_table_key, conversion_table_items, data_create_task_info_list }
 * @returns {Promise<{ payload_s3_key: string; user_id_sub: string }>}
 */
export async function uploadConversionValuesPayloadToS3(payload) {
  const credentials = await Auth.currentUserCredentials()
  const identityId = credentials?.identityId ?? ''

  const s3Path = `master/value-conversion/pending/${nanoid()}.json`
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })

  const { Storage } = await import('aws-amplify')
  // contentType は application/json を指定しない。
  // 5MB 超で Amplify v4 がマルチパートアップロードに切り替わると、開始リクエスト
  // CreateMultipartUpload（POST ?uploads=）は本来ボディ無しだが、Content-Type:
  // application/json が付くと Amplify の axios ハンドラ（空ボディ+Content-Type で
  // data=null をセット）と axios の transformRequest（Content-Type が
  // application/json かつ data=null だと JSON.stringify(null)="null" を送信）の
  // 組み合わせで非空ボディが送られ、S3 が 400 InvalidRequest を返す。
  // json 以外（octet-stream）なら axios はボディを付けないため回避できる。
  await Storage.put(s3Path, blob, {
    level: 'private',
    contentType: 'application/octet-stream'
  })

  return {
    payload_s3_key: s3Path,
    user_id_sub: identityId
  }
}

/**
 * IF02071: 変換項目更新（POST）
 *
 * 変換項目本体は事前に S3 へアップロード済みで、ここでは S3 キーを含む小さな リクエストボディ（conversion_table_key, payload_s3_key, user_id_sub,
 * data_create_task_info_list）を送信する。
 *
 * @param {string} corporationNumber
 * @param {{
 *   conversion_table_key: string
 *   payload_s3_key: string
 *   user_id_sub: string
 *   data_create_task_info_list: Object[]
 * }} body
 * @returns {Promise<Object>}
 */
export async function updateConversionTable(corporationNumber, body) {
  const path = PathInfoList.Resource.updateConversionValues(corporationNumber)
  const init = {
    headers: { 'Content-Type': 'application/json' },
    body
  }
  return httpPost(restApiKey.CHROFY_REST_API, path, init)
}

/**
 * IF02082: 変換表更新（POST）変換表メタデータの新規登録・更新
 *
 * @param {string} corporationNumber
 * @param {Object} conversionTableData - { key, name, conversion_target, memo, display_order }
 * @returns {Promise<Object>}
 */
export async function updateConversionTableMeta(corporationNumber, conversionTableData) {
  const path = PathInfoList.Resource.updateConversionTable(corporationNumber)
  const init = {
    headers: { 'Content-Type': 'application/json' },
    body: {
      conversion_table_data: conversionTableData
    }
  }
  return httpPost(restApiKey.CHROFY_REST_API, path, init)
}

/**
 * IF02081: 変換表削除（POST）
 *
 * @param {string} corporationNumber
 * @param {string} conversionTableKey - 削除対象の変換表キー
 * @returns {Promise<Object>}
 */
export async function deleteConversionTableApi(corporationNumber, conversionTableKey) {
  const path = PathInfoList.Resource.deleteConversionTable(corporationNumber)
  const init = {
    headers: { 'Content-Type': 'application/json' },
    body: {
      conversion_table_key: conversionTableKey
    }
  }
  return httpPost(restApiKey.CHROFY_REST_API, path, init)
}
